import { createContext, useCallback, useContext, useMemo, useState, useTransition } from "react";
import type { ReactNode } from "react";
import type {
  ActivityEvent,
  AdjustmentDirection,
  Case,
  CaseAnalysis,
  CaseRecord,
  Cents,
  DecisionOutcome,
  DecisionRecommendation,
  EvidenceSource,
  VerificationStatus,
} from "@/lib/types";
import { addAdjustment, removeAdjustment } from "@/services/analysis";
import { listActivity, sortActivity } from "@/services/activity";
import { recordOutcome, setRecommendedAmount, toggleCondition } from "@/services/decisions";
import { editExtracted, setVerification } from "@/services/evidence";

/**
 * Persistent case context.
 *
 * The spec puts this on `cases/[caseId]/layout.tsx`, and that is exactly where
 * it belongs: the layout does not remount as the analyst moves between the five
 * tabs, so an adjustment made on Analysis is visible on Decision without a
 * refetch and without the case header ever flickering.
 *
 * Writes go through the service layer, never straight into this state — the
 * services own the audit trail. What comes back is mirrored here.
 */
interface CaseContextValue extends CaseRecord {
  actor: string;
  pending: boolean;
  verify: (valueId: string, status: VerificationStatus) => void;
  correct: (valueId: string, value: string) => void;
  adjust: (input: { label: string; direction: AdjustmentDirection; amount: Cents; reason: string }) => void;
  unadjust: (adjustmentId: string) => void;
  changeAmount: (amount: Cents) => void;
  setCondition: (conditionId: string, selected: boolean) => void;
  decide: (outcome: DecisionOutcome, note?: string) => void;
}

const CaseContext = createContext<CaseContextValue | null>(null);

export function CaseProvider({
  record,
  actor,
  children,
}: {
  record: CaseRecord;
  actor: string;
  children: ReactNode;
}) {
  const [kase, setCase] = useState<Case>(record.case);
  const [evidence, setEvidence] = useState<EvidenceSource[]>(record.evidence);
  const [analysis, setAnalysis] = useState<CaseAnalysis>(record.analysis);
  const [decision, setDecision] = useState<DecisionRecommendation>(record.decision);
  const [activity, setActivity] = useState<ActivityEvent[]>(() => sortActivity(record.activity));
  const [pending, startTransition] = useTransition();

  const caseId = record.case.id;

  /** Every mutation refreshes the timeline: an untracked change is a defect. */
  const refreshActivity = useCallback(async () => {
    setActivity(await listActivity(caseId));
  }, [caseId]);

  const run = useCallback(
    (work: () => Promise<void>) => {
      startTransition(async () => {
        await work();
        await refreshActivity();
      });
    },
    [refreshActivity],
  );

  const value = useMemo<CaseContextValue>(
    () => ({
      case: kase,
      evidence,
      analysis,
      decision,
      activity,
      actor,
      pending,

      verify: (valueId, status) =>
        run(async () => setEvidence(await setVerification(caseId, valueId, status, actor))),

      correct: (valueId, next) =>
        run(async () => setEvidence(await editExtracted(caseId, valueId, next, actor))),

      adjust: (input) =>
        run(async () => {
          const next = await addAdjustment(caseId, input, actor);
          if (next) setAnalysis(next);
        }),

      unadjust: (adjustmentId) =>
        run(async () => {
          const next = await removeAdjustment(caseId, adjustmentId, actor);
          if (next) setAnalysis(next);
        }),

      changeAmount: (amount) =>
        run(async () => {
          const next = await setRecommendedAmount(caseId, amount, actor);
          if (next) setDecision(next.decision);
        }),

      setCondition: (conditionId, selected) =>
        run(async () => {
          const next = await toggleCondition(caseId, conditionId, selected, actor);
          if (next) setDecision(next.decision);
        }),

      decide: (outcome, note) =>
        run(async () => {
          const next = await recordOutcome(caseId, outcome, actor, note);
          if (next) {
            setDecision(next.decision);
            setCase(next.case);
          }
        }),
    }),
    [kase, evidence, analysis, decision, activity, actor, pending, caseId, run],
  );

  return <CaseContext.Provider value={value}>{children}</CaseContext.Provider>;
}

export function useCase(): CaseContextValue {
  const value = useContext(CaseContext);
  if (!value) throw new Error("useCase must be used inside a CaseProvider.");
  return value;
}
